import React, { useState } from 'react';

import { useDispatch } from 'react-redux';

import { api } from '@api/reduxApi';
import { Text, Toggle } from '@components/common';
import { UploadButton } from '@components/common/UploadButton';
import { surveyBuilderApi } from '@components/SurveyBuilder/api';
import { BLOCK_KIND_LABELS, LOCKED_BLOCK_TYPES, NON_QUESTION_KINDS } from '@components/SurveyBuilder/constants';
import * as Enums from '@components/SurveyBuilder/types/enums';
import * as Models from '@components/SurveyBuilder/types/models';
import { Tooltip } from '@components/Tooltip';

const DEFAULT_ENABLED_KINDS: `${Enums.Kind}`[] = [
  Enums.Kind.info,
  Enums.Kind.permissions,
  Enums.Kind.prototypeTest,
  Enums.Kind.welcome,
  Enums.Kind.thankYou,
  Enums.Kind.websiteTest,
  Enums.Kind.cardSort
];

interface Props {
  block: Models.Block;
}

export const Header: React.FC<Props> = ({ block }) => {
  const [updateImage] = api.useUpdateStudyScreenerFieldImageMutation();
  const [updateBlock] = surveyBuilderApi.useUpdateSurveyBuilderBlockMutation();

  const dispatch = useDispatch();

  const handleOnToggle = (required: boolean) => {
    updateBlock({ ...block, required });
  };

  const handleOnImageUpload = async ([file]: File[]) => {
    if (block.screener_field_id && file) {
      const body = new FormData();
      body.append('screener_field[image]', file);

      await updateImage({ id: block.screener_field_id, body }).unwrap();

      dispatch(api.util.invalidateTags([{ type: 'SurveyBuilderBlocks', id: block.id }]));
    }
  };

  const showRequiredField = (kind: Models.BlockKind) => !DEFAULT_ENABLED_KINDS.includes(kind);

  return (
    <header className='flex h-20 flex-row items-center justify-between border-b border-gray-200 p-6'>
      <Text as='h2' className='text-xl font-bold'>
        {BLOCK_KIND_LABELS[block.kind]}
      </Text>

      <div className='flex flex-row space-x-1'>
        {![...NON_QUESTION_KINDS, ...LOCKED_BLOCK_TYPES].includes(block.kind) && (
          <Tooltip content='Upload image'>
            <UploadButton
              supportedFileTypes={['png', 'eps', 'jpg', 'jpeg', 'gif']}
              onUploadFiles={handleOnImageUpload}
              icon='picture'
              aria-label='Upload image'
              text
              hasFileUploadZone
            />
          </Tooltip>
        )}

        {showRequiredField(block.kind) && (
          <div className='flex flex-row items-center space-x-1'>
            <Toggle onToggle={handleOnToggle} on={block.required} testId='toggle_required' />
            <Text className='text-xs'>Required</Text>
          </div>
        )}
      </div>
    </header>
  );
};
